import "./App.css";
import {
  CognitoAuthProvider,
  AppLayout,
  AccountMenu,
  LogoutPage,
  LoggedOutPage,
  ProtectedRoute,
  CallbackPage,
  AppFooter,
  AppBar,
  NavRail,
  useThemeSwitcher,
  getDefaultAccountMenuItems,
  ActiveOrgProvider,
  PermissionProvider,
  useActiveOrg,
  OrgSwitcher,
  UserDisplayName,
  BuildBadge,
  UsaBadge,
  installServerErrorInterceptor,
} from "common-fe";
import { APP_NAMESPACE } from "./permissions.js";

installServerErrorInterceptor();
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Builds the org-list fetcher from the generated OpenAPI client and injects it
// into the shared ActiveOrgProvider (which never calls fetch itself). Rendered
// inside CognitoAuthProvider so useAuth/the token are available.
const AccountActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};
import { createBrandTheme, Logo, colors } from "brand";
import AccountSettings from "./pages/AccountSettings";
import OrgAdmin from "./pages/OrgAdmin";
import OrgSettings from "./pages/OrgSettings";
import SuperOrganizations from "./pages/SuperOrganizations";
import Home from "./pages/Home";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useCallback } from "react";
import { useTranslation } from "i18n-fe";

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");
  const { mode, toggleTheme } = useThemeSwitcher();

  const handleAuthenticated = useCallback(
    (targetPath) => {
      navigate(targetPath, { replace: true });
    },
    [navigate]
  );

  const theme = createBrandTheme();

  const BrandLogo = () => (
    <Box
      onClick={() => navigate("/")}
      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
    >
      <Logo
        style={{
          paddingLeft: "8px",
          height: 40,
          width: "auto",
          minHeight: 40,
          minWidth: 40,
          maxHeight: 40,
          maxWidth: "none",
          marginRight: 16,
          flexShrink: 0,
          flexGrow: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.accountCenterTitle")}
      </Typography>
    </Box>
  );

  const navItems = [
    {
      icon: <HomeIcon />,
      text: t("navigation.appsLink"),
      onClick: () => navigate("/"),
    },
    {
      icon: <GroupIcon />,
      text: t("navigation.organizationsLink"),
      onClick: () => navigate("/orgs"),
    }
  ];

  // Super-aware nav: the Admin link only appears for Fox Village super admins.
  // Rendered inside ActiveOrgProvider so it can read isSuper from the token.
  const SuperAwareNavRail = () => {
    const { isSuper } = useActiveOrg();
    const items = isSuper
      ? [
          ...navItems,
          {
            icon: <AdminPanelSettingsIcon />,
            text: t("navigation.adminLink"),
            onClick: () => navigate("/admin/orgs"),
          },
        ]
      : navItems;
    return (
      <NavRail navItems={items} backgroundColor={colors.darkGrey} color="#ffffff" />
    );
  };

  const accountMenuItems = getDefaultAccountMenuItems({ 
    t, 
    mode, 
    toggleTheme, 
    navigate,
    cognitoConfig: {
      clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      cognitoDomain: import.meta.env.VITE_COGNITO_AUTH_DOMAIN,
      logoutUri: import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI,
    }
  });

  const appBarRightContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {/* #2189 - relative path, not VITE_ACCOUNT_FE_BASE_URL: account-fe IS
          the account app, so /orgs is always same-origin, AND deploy.yml's
          build_account_fe() explicitly builds with "the job-level derived
          env as-is" (no VITE_ACCOUNT_FE_BASE_URL export like the other FEs
          get) - using the env var here would bake "undefined/orgs" in every
          real deploy. */}
      <OrgSwitcher accountOrgsUrl="/orgs" />
      <UserDisplayName />
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <CognitoAuthProvider
      cognitoAuthConfig={cognitoAuthConfig}
      location={location}
      onAuthenticated={handleAuthenticated}
    >
      <ThemeProvider theme={theme} defaultMode="light">
        <CssBaseline />
        <AccountActiveOrgProvider>
        <PermissionProvider appNamespace={APP_NAMESPACE}>
        <AppLayout
          appBar={
            <AppBar
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
              rightContent={appBarRightContent}
              brandLogo={<BrandLogo />}
            />
          }
          navRail={<SuperAwareNavRail />}
          
          appFooterEnabled={true}
          appFooter={
            <AppFooter
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">
                  {t("app.copyrightNotice", { year: new Date().getFullYear() })}
                </Typography>
              </Box>
            </AppFooter>
          }
          brandLogo={<BrandLogo />}
          appBarRightContent={appBarRightContent}
        >
          <Routes>
            <Route
              path="/callback"
              element={
                <CallbackPage navigateFunction={navigate} location={location} />
              }
            />
            <Route
              path="/logout"
              element={<LogoutPage navigateFunction={navigate} />}
            />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orgs"
              element={
                <ProtectedRoute>
                  <OrgAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orgs/settings"
              element={
                <ProtectedRoute>
                  <OrgSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orgs"
              element={
                <ProtectedRoute>
                  <SuperOrganizations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AppLayout>
        </PermissionProvider>
        </AccountActiveOrgProvider>
      </ThemeProvider>
    </CognitoAuthProvider>
  );
}

function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}

export default App;
